import React, { ReactElement } from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import { IAnimationProps, useFadeInCss, useFadeOutCss } from '../../fundamentals/animations/animations';
import { BASIC_COLOURS } from '../../fundamentals/colours/variants';
import { IShadowProps, useShadowCss } from '../../fundamentals/shadows/shadows';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { P } from '../Texts/Texts';
import { TooltipArrowDirection } from '../TooltipArrow/TooltipArrow';
import {
  Placement,
  TOP_START,
  TOP,
  TOP_END,
  BOTTOM_START,
  BOTTOM,
  BOTTOM_END,
  LEFT_START,
  LEFT,
  LEFT_END,
  RIGHT_START,
  RIGHT,
  RIGHT_END,
  isOnTopOrBottom
} from '../../fundamentals/placements/placements';
import { UsePopperOptions } from 'react-overlays/usePopper';
import { Z_INDICES } from '../../common/constants';
import { BaseDiv } from '../../common/BaseElements';
import { EORA_NUMBERS } from '../../fundamentals/numbers/eoraNumbers';

// NOTE: (intentional use of PX rather than REM)
// These values represent PX to avoid rendering issues caused by mixing PX from PopperJs styles and REM resulting in intermittent gaps between popper and arrow
const ARROW_WIDTH = 6;
const ARROW_HEIGHT = 12;
const ARROW_EDGE_GAP = 14;
const TOOLTIP_GAP = 10;

export interface ITooltipProps {
  text?: string | React.ReactNode;
  supportingText?: string | ReactElement<any>;
  light?: boolean;
  arrow?: boolean;
  showHelpIcon?: boolean;
  placement?: Placement;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  micro?: boolean;
  children: React.ReactElement | string | number;
}

interface IPosition<T> {
  top?: T;
  right?: T;
  bottom?: T;
  left?: T;
}

export interface ITooltipPositionProps {
  tooltipPosition?: IPosition<string>;
  arrowPosition?: IPosition<string>;
  arrowDirection?: TooltipArrowDirection;
}

export const POPPER_MODIFIERS: UsePopperOptions['modifiers'] = [
  {
    name: 'offset',
    options: {
      offset: [0, TOOLTIP_GAP]
    }
  },
  {
    name: 'arrow',
    options: {
      padding: ARROW_EDGE_GAP
    }
  },
  {
    name: 'preventOverflow',
    options: {
      padding: TOOLTIP_GAP
    }
  },
  {
    name: 'flip'
  }
];

const textColour = (theme: DefaultTheme, light?: boolean) => theme.tooltipColours[light ? BASIC_COLOURS.WHITE : BASIC_COLOURS.BLACK].text;
const alternateTextColour = (theme: DefaultTheme, light?: boolean) => theme.tooltipColours[light ? BASIC_COLOURS.WHITE : BASIC_COLOURS.BLACK].placeholder;
const backgroundColour = (theme: DefaultTheme, light?: boolean, micro?: boolean) =>
  micro
    ? theme.tooltipColours[light ? BASIC_COLOURS.WHITE : BASIC_COLOURS.BLACK].caret
    : theme.tooltipColours[light ? BASIC_COLOURS.WHITE : BASIC_COLOURS.BLACK].background;

const usePaddingCss = css<Pick<ITooltipProps, 'micro'>>`
  padding: ${({ theme, micro }) => {
    return micro ? `${theme.eoraSpaces.small} ${theme.eoraSpaces.medium}` : `${theme.eoraSpaces.medium} ${theme.eoraSpaces.offgrid_12}`;
  }};
`;

export const TooltipBubbleContainer = styled(BaseDiv)<Pick<ITooltipProps, 'light' | 'supportingText' | 'micro'> & IShadowProps & IAnimationProps>`
  ${useFadeInCss};
  ${useFadeOutCss};
  ${({ light }) => light && useShadowCss};

  text-align: left;
  position: absolute;
  z-index: ${Z_INDICES.TOOLTIP};
  white-space: normal;

  border-radius: ${({ theme }) => theme.standardBorderRadius[SIZES.SMALL]};

  ${usePaddingCss};

  background-color: ${({ theme, light, micro }) => backgroundColour(theme, light, micro)};
  color: ${({ theme, light }) => textColour(theme, light)};

  width: max-content;
  min-width: ${({ supportingText }) => (supportingText ? PX_TO_REM[EORA_NUMBERS.BASE.UNITS.UNIT_18] : 'unset')};
  max-width: ${({ supportingText }) =>
    supportingText
      ? PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.TOOLTIP_WIDTHS.SIZE_TOOLTIP_WIDTH_LARGE]
      : PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.TOOLTIP_WIDTHS.SIZE_TOOLTIP_WIDTH_SMALL]};
`;

// NOTE: Intentional use of PX rather than REM, see ARROW_WIDTH above
const topStyle = css`
  bottom: -${ARROW_WIDTH}px;
`;

const rightStyle = css`
  left: -${ARROW_WIDTH}px;
`;

const bottomStyle = css`
  top: -${ARROW_WIDTH}px;
`;

const leftStyle = css`
  right: -${ARROW_WIDTH}px;
`;

const arrowPositionMap: Partial<Record<Placement, ReturnType<typeof css>>> = {
  [TOP]: topStyle,
  [TOP_END]: topStyle,
  [TOP_START]: topStyle,
  [RIGHT_START]: rightStyle,
  [RIGHT]: rightStyle,
  [RIGHT_END]: rightStyle,
  [BOTTOM_START]: bottomStyle,
  [BOTTOM]: bottomStyle,
  [BOTTOM_END]: bottomStyle,
  [LEFT_START]: leftStyle,
  [LEFT]: leftStyle,
  [LEFT_END]: leftStyle
};

export const Arrow = styled(BaseDiv)<{ placement: Placement }>`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ placement }) => `${isOnTopOrBottom(placement) ? ARROW_HEIGHT : ARROW_WIDTH}px`};
  height: ${({ placement }) => `${isOnTopOrBottom(placement) ? ARROW_WIDTH : ARROW_HEIGHT}px`};

  ${({ placement }) => arrowPositionMap[placement]};
`;

export const TooltipText = styled(P)<Pick<ITooltipProps, 'light'> & { alternate?: boolean }>`
  padding: 0;
  color: ${({ theme, light, alternate }) => (alternate ? alternateTextColour(theme, light) : textColour(theme, light))};
`;
